import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getAnalytics, isSupported, logEvent, setUserId, type Analytics } from 'firebase/analytics';
import { getFirestore } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { getMessaging, getToken } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const db = getFirestore(app);
export const rtdb = getDatabase(app);
export const storage = getStorage(app);

let analyticsInstance: Analytics | null = null;

export const analyticsReady: Promise<Analytics | null> =
  typeof window === 'undefined' || !firebaseConfig.measurementId
    ? Promise.resolve(null)
    : isSupported()
        .then((supported) => {
          if (!supported) return null;
          analyticsInstance = getAnalytics(app);
          return analyticsInstance;
        })
        .catch((error) => {
          console.error('Firebase analytics initialization failed:', error);
          return null;
        });

// Initialize messaging with error handling
let messaging: any = null;
try {
  messaging = getMessaging(app);
} catch (error) {
  console.error('Firebase messaging initialization failed:', error);
}

export { messaging };

export { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  signInWithPopup 
};

const getAnalyticsInstance = async () => analyticsInstance ?? analyticsReady;

type AnalyticsEventParams = Record<string, string | number | boolean | undefined>;

export const trackEvent = async (eventName: string, params?: AnalyticsEventParams) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  logEvent(analytics, eventName, params);
};

export const trackPageView = async (pagePath: string) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics || typeof window === 'undefined') return;

  logEvent(analytics, 'page_view', {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const setAnalyticsUser = async (userId: string | null) => {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  setUserId(analytics, userId);
};

// Function to request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    console.log('Starting permission request...');
    
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications');
      return null;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.error('Service workers are not supported');
      return null;
    }

    const permission = await Notification.requestPermission();
    console.log('Browser permission:', permission);
    
    if (permission === 'granted') {
      try {
        // Make sure messaging is initialized
        if (!messaging) {
          console.error('Firebase messaging not initialized');
          return null;
        }

        // Register service worker if not already registered
        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          console.log('Registering service worker...');
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          console.log('Service worker registered:', registration);
          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;
        }

        const token = await getToken(messaging, {
          vapidKey: 'BI2gd2zxXFuWTIRmLR4XUbU7IlKcClglnZpFVGM9vgpMR6q7tXvweswKiLYF3BvgSZrJgdsx7GmgB837BLNhlHk',
          serviceWorkerRegistration: registration
        });
        console.log('FCM Token:', token);
        
        if (!token) {
          console.error('No registration token available. Request permission to generate one.');
          return null;
        }

        return token;
      } catch (fcmError) {
        console.error('Error getting FCM token:', fcmError);
        console.error('FCM Error details:', JSON.stringify(fcmError, null, 2));
        return null;
      }
    }
    console.log('Permission not granted:', permission);
    return null;
  } catch (error) {
    console.error('Notification permission error:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    return null;
  }
};
